import { type FC, useEffect, useMemo, useRef } from "react";
import { FileText } from "lucide-react";

import { useAppContext } from "@/shared/hooks/useAppContext";
import { useUserContacts } from "@/shared/hooks/useUserContacts";
import { useWillExtraction } from "@/shared/hooks/useWillExtraction";
import { formatContactName, isWillWitness } from "@/shared/utils/contactUtils";
import { FAMILY_RELATIONSHIPS } from "@/shared/constants/relationshipOptions";
import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";
import type { RevealVariant } from "@/shared/types/probateTypes";

interface RevealRow {
  name: string;
  reason: string;
  badge: "signature" | "notice" | "written_statement";
}

interface RevealScreenProps extends FlowCustomComponentProps {
  variant?: RevealVariant;
}

const BADGE_LABELS: Record<RevealRow["badge"], string> = {
  signature: "Signature needed",
  notice: "Notice by mail",
  written_statement: "Written statement needed",
};

const BADGE_STYLES: Record<RevealRow["badge"], string> = {
  signature: "border-green-300 bg-green-100 text-brand",
  notice: "border-stone-300 bg-stone-50 text-stone-700",
  written_statement: "border-stone-300 bg-stone-50 text-stone-700",
};

export const RevealScreen: FC<RevealScreenProps> = ({ setAnswer }) => {
  const { person, user } = useAppContext();
  const { contacts } = useUserContacts();
  const estateId = person?.estate_id;
  const { data: willExtraction } = useWillExtraction(estateId);
  const name = person?.first_name ?? "the decedent";
  const actingUserEmail = user?.email?.toLowerCase();

  const rows = useMemo((): RevealRow[] => {
    const result: RevealRow[] = [];

    for (const contact of contacts) {
      if (actingUserEmail && contact.email?.toLowerCase() === actingUserEmail) continue;
      if (contact.is_hidden) continue;
      if (contact.person_status === "passed_away") continue;

      const rel = contact.relationship?.toLowerCase() ?? "";

      if (rel === "former-spouse") {
        if (!contact.extracted_from_document && !contact.source_document_id) continue;
        result.push({
          name: formatContactName(contact.first_name, contact.last_name, contact.middle_name),
          reason: "Named in the will",
          badge: "signature",
        });
        continue;
      }

      if (isWillWitness(contact) && !FAMILY_RELATIONSHIPS.has(rel)) {
        result.push({
          name: formatContactName(contact.first_name, contact.last_name, contact.middle_name),
          reason: "Witnessed the will's signing",
          badge: "written_statement",
        });
        continue;
      }

      if (rel && FAMILY_RELATIONSHIPS.has(rel)) {
        result.push({
          name: formatContactName(contact.first_name, contact.last_name, contact.middle_name),
          reason: `${name}'s closest family`,
          badge: "signature",
        });
        continue;
      }

      if (contact.extracted_from_document || contact.source_document_id) {
        result.push({
          name: formatContactName(contact.first_name, contact.last_name, contact.middle_name),
          reason: "Named in the will",
          badge: "signature",
        });
      }
    }

    const charitable = willExtraction?.specific?.charitable_contributions;
    if (Array.isArray(charitable)) {
      for (const org of charitable) {
        const orgName = (org as Record<string, unknown>).organization_name;
        if (typeof orgName === "string" && orgName) {
          result.push({ name: orgName, reason: "Named in the will", badge: "notice" });
        }
      }
    }

    const isSelfProving =
      (willExtraction?.specific?.document_background as Record<string, unknown> | undefined)
        ?.is_self_proving === true;

    if (!isSelfProving) {
      const hasWitnessRows = result.some((r) => r.badge === "written_statement");
      if (!hasWitnessRows) {
        const existingNames = new Set(result.map((r) => r.name.toLowerCase()));
        const witnessInfo = willExtraction?.specific?.witness_info;
        if (Array.isArray(witnessInfo)) {
          for (const w of witnessInfo as Array<{ name?: string }>) {
            if (w.name && !existingNames.has(w.name.toLowerCase())) {
              result.push({
                name: w.name,
                reason: "Witnessed the will's signing",
                badge: "written_statement",
              });
            }
          }
        }
      }
    }

    const badgeOrder: Record<RevealRow["badge"], number> = {
      signature: 0,
      notice: 1,
      written_statement: 2,
    };

    const finalResult = isSelfProving
      ? result.filter((r) => r.badge !== "written_statement")
      : result;

    finalResult.sort((a, b) => badgeOrder[a.badge] - badgeOrder[b.badge]);

    return finalResult;
  }, [contacts, actingUserEmail, name, willExtraction]);

  const setAnswerRef = useRef(setAnswer);
  setAnswerRef.current = setAnswer;

  useEffect(() => {
    setAnswerRef.current("familyTreeConfirmed", "true");
  }, []);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <p className="font-sans text-xl font-semibold leading-[30px]">
          Here's who the court needs to hear from
        </p>
        <p className="text-sm text-secondary tracking-[-0.28px]">
          Based on your responses and the will, we've mapped out the{" "}
          {rows.length} people and organizations required for court approval.
        </p>
      </div>

      {rows.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-primary">
          {rows.map((row, i) => (
            <div
              key={`${row.name}-${row.badge}`}
              className={`flex items-center justify-between gap-4 bg-white p-4 ${i > 0 ? "border-t border-primary" : ""}`}
            >
              <div className="flex flex-col gap-1 min-w-0">
                <p className="text-base font-semibold tracking-[-0.32px]">{row.name}</p>
                <p className="text-sm text-secondary tracking-[-0.28px]">{row.reason}</p>
              </div>
              <span
                className={`shrink-0 rounded-full border px-2.5 py-0.5 text-sm font-medium tracking-[-0.28px] ${BADGE_STYLES[row.badge]}`}
              >
                {BADGE_LABELS[row.badge]}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-start gap-3 rounded-lg border border-primary bg-background p-4">
        <div className="flex size-8 shrink-0 items-center justify-center rounded-sm border border-primary bg-white p-2">
          <FileText className="size-full text-secondary" />
        </div>
        <div className="flex flex-col">
          <p className="text-sm font-medium">Saved to your Contacts</p>
          <p className="text-sm text-secondary">
            We saved the people from this list to your Contacts page. Gather
            addresses, send invites, and track signatures whenever you are ready.
          </p>
        </div>
      </div>

    </div>
  );
};
